import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { Box, CircularProgress, Grid, Stack, Typography } from "@mui/material";
import React, { useEffect, useState } from "react";

import { EProductSection } from "src/commons/interfaces/product";
import { getPlanById, getPlans } from "src/utils";
import { BillingPlanInfoCard } from "./BillingPlanInfoCard";

type Props = {
  section?: EProductSection;
  title?: string;
  description?: string;
};

const normalizeValue = (value: any) => {
  return String(value ?? "")
    .toLowerCase()
    .replace(/\s+/g, " ")
    .trim();
};

const dedupeMappedPlans = (plans: any[]) => {
  const map = new Map<string, any>();

  for (const plan of plans) {
    const key = [
      normalizeValue(plan.title),
      Number(plan.price?.amount ?? 0),
      normalizeValue(plan.price?.currency || "USD"),
      normalizeValue(plan.billingType || "MONTHLY"),
    ].join("::");

    const existing = map.get(key);

    if (!existing) {
      map.set(key, plan);
      continue;
    }

    map.set(key, {
      ...existing,
      ...plan,
      benefits: (existing?.benefits?.length ?? 0) >= (plan?.benefits?.length ?? 0) ? existing.benefits : plan.benefits,
      isPopular: existing?.isPopular || plan?.isPopular,
      isComingSoon: existing?.isComingSoon || plan?.isComingSoon,
      hexColor: existing?.hexColor || plan?.hexColor,
    });
  }

  return Array.from(map.values());
};

export const BillingPlansInfoSection: React.FC<Props> = ({
  section = EProductSection.FLOW_MOBILE,
  title = "Available plans",
  description = "Internal overview for Super Admin. This section shows each plan, billing type, price, and included benefits without allowing checkout or plan changes.",
}) => {
  const [plans, setPlans] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [expandedPlanKey, setExpandedPlanKey] = useState<string | null>(null);
  useEffect(() => {
    let mounted = true;

    const loadPlans = async () => {
      try {
        setIsLoading(true);

        const availablePlans = await getPlans(section);
        const sortedPlans = availablePlans?.sort((a: any, b: any) => a?.order - b?.order) ?? [];

        const mappedPlans = [];

        for (const plan of sortedPlans) {
          const planId = plan?.paypalPlanId ? plan.paypalPlanId : plan?.id;

          const planDetail = await getPlanById(planId, plan?.isFreeTrial, plan?.isCustom, plan?.billingType);

          const isPayAsYouGo = plan?.billingType === "PAY_AS_YOU_GO" || planDetail?.billingType === "PAY_AS_YOU_GO";

          const regularBillingCycle = planDetail?.billing_cycles?.find((billingCycle: any) => billingCycle?.tenure_type === "REGULAR");

          mappedPlans.push({
            id: String(planId),
            originalPlanId: plan?.id,
            isCustom: plan?.isCustom,
            isFreeTrial: plan?.isFreeTrial,
            billingType: plan?.billingType || planDetail?.billingType,
            title: planDetail?.product?.name || plan?.name || "Untitled plan",
            description: planDetail?.product?.description || "",
            benefits: planDetail?.product?.benefits?.map((benefit: any) => benefit.name) ?? [],
            price: {
              amount: isPayAsYouGo ? (plan?.meta?.basePrice ?? 0) : (regularBillingCycle?.pricing_scheme?.fixed_price?.value ?? 0),
              currency: isPayAsYouGo ? (plan?.meta?.currency ?? "USD") : (regularBillingCycle?.pricing_scheme?.fixed_price?.currency_code ?? "USD"),
              frequency: "monthly",
            },
            hexColor: planDetail?.hexColor,
            isPopular: plan?.isPopular || planDetail?.product?.name?.toLowerCase() === "start up",
            isComingSoon: plan?.isComingSoon,
          });
        }

        const dedupedPlans = dedupeMappedPlans(mappedPlans);

        if (mounted) {
          setPlans(dedupedPlans);
        }
      } catch (error) {
        console.error("[BillingPlansInfoSection][loadPlans]", error);
      } finally {
        if (mounted) {
          setIsLoading(false);
        }
      }
    };

    loadPlans();

    return () => {
      mounted = false;
    };
  }, [section]);

  return (
    <Box
      sx={{
        p: { xs: 2, md: 3 },
      }}
    >
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.75 }}>
        <InfoOutlinedIcon sx={{ fontSize: 20, color: "#E87722" }} />

        <Typography
          sx={{
            fontSize: "0.75rem",
            fontWeight: 800,
            color: "#E87722",
            textTransform: "uppercase",
            letterSpacing: "0.08em",
          }}
        >
          Plan information
        </Typography>
      </Stack>

      <Typography
        sx={{
          fontSize: { xs: "1.35rem", md: "1.65rem" },
          fontWeight: 900,
          color: "var(--fx-surface-text, #0F172A)",
          letterSpacing: "-0.4px",
        }}
      >
        {title}
      </Typography>

      <Typography
        sx={{
          mt: 0.75,
          mb: 3,
          maxWidth: 720,
          fontSize: "0.9rem",
          color: "var(--fx-surface-textsecondary, #64748B)",
          lineHeight: 1.6,
          fontWeight: 500,
        }}
      >
        {description}
      </Typography>

      {isLoading ? (
        <Box
          sx={{
            minHeight: 180,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <CircularProgress size={26} sx={{ color: "#E87722" }} />
        </Box>
      ) : plans.length === 0 ? (
        <Box
          sx={{
            p: 3,
            borderRadius: "16px",
            bgcolor: "var(--fx-surface-sunken, #F8FAFC)",
            border: "1px dashed var(--fx-surface-borderstrong, #CBD5E1)",
            textAlign: "center",
          }}
        >
          <Typography sx={{ fontWeight: 800, color: "var(--fx-surface-textsecondary, #64748B)" }}>No plans available</Typography>

          <Typography sx={{ mt: 0.5, color: "var(--fx-surface-textsecondary, #64748B)", fontSize: "0.85rem" }}>No plan information was returned for this section.</Typography>
        </Box>
      ) : (
        <Box
          sx={{
            columnCount: {
              xs: 1,
              md: 2,
              xl: 3,
            },
            columnGap: 2,
          }}
        >
          {plans.map((plan) => {
            const planKey = `${section}-${plan.title}-${plan.price?.amount}-${plan.billingType}`;
            const isExpanded = expandedPlanKey === planKey;

            return (
              <Box
                key={planKey}
                sx={{
                  breakInside: "avoid",
                  mb: 2,
                }}
              >
                <BillingPlanInfoCard
                  title={plan.title}
                  description={plan.description}
                  benefits={plan.benefits}
                  price={plan.price}
                  billingType={plan.billingType}
                  isPopular={plan.isPopular}
                  isComingSoon={plan.isComingSoon}
                  isCustom={plan.isCustom}
                  isFreeTrial={plan.isFreeTrial}
                  hexColor={plan.hexColor}
                  expanded={isExpanded}
                  onToggle={() => {
                    setExpandedPlanKey(isExpanded ? null : planKey);
                  }}
                />
              </Box>
            );
          })}
        </Box>
      )}
    </Box>
  );
};
